import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { createPosition } from '@/modules/Employee/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface PositionCreateModalProps extends ModalProps {
    departmentId: number
}

interface FormValues {
    position_name: string
    position_is_executive: boolean
}

const PositionCreateModal: React.FC<PositionCreateModalProps> = ({
    open,
    onClose,
    departmentId,
}) => {
    const { t } = useTranslation('employee')

    const { control, handleSubmit, reset } = useForm<FormValues>({
        defaultValues: {
            position_name: '',
            position_is_executive: false,
        },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: createPosition },
        {
            reactQueryKeys: QUERY_KEYS.POSITIONS,
            successMessage: 'position_created_success',
            errorMessage: 'error_occurred',
            onSuccess: () => {
                reset()
                onClose()
            },
        }
    )

    const onSubmit = async (data: FormValues) => {
        await mutateAsync({
            position_name: data.position_name,
            department_id: departmentId,
            position_is_executive: data.position_is_executive,
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('create_position.title')}
            maxWidth="sm"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-4">
                    <FormItem
                        control={control}
                        name="position_name"
                        type="textfield"
                        label={t('position_name')}
                        placeholder={t('enter_position_name')}
                    />

                    <FormItem
                        control={control}
                        name="position_is_executive"
                        type="checkbox"
                        label={t('position_is_executive')}
                    />

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            loading={isPending}
                        >
                            {t('create')}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            type="button"
                        >
                            {t('cancel')}
                        </Button>
                    </div>
                </div>
            </form>
        </Dialog>
    )
}

export default PositionCreateModal
